import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  Alert,
  Col,
  PageHeader,
  Row,
} from '@cochlear-design-system/foundation';
import { DataTable } from '@cochlear-design-system/features.dataTable/dist/DataTable/DataTable';
import { useHistory, useLocation } from 'react-router-dom';
import CustomToolBar from '../../../ui/CustomToolBar';
import * as clinicEquipmentConfig from '../configs/clinicEquipmentConfig';
import { getLabelsForFields } from '../../../utils/labelHelper';
import { resetPreviousExpandedSections } from '../../PatientRecord/utils/filters';
import { ASSET_STATUS, LOANER_STATUS, routes } from '../../../config';
import ReturnLoanerModal from './ReturnLoanerModal';
import providerService from '../../../services/providerService';

import { useProviderDevices } from '../../../hooks/useProviderDevices';
import { useReturnLoaner } from '../../../hooks/useReturnLoaner';
import { useProviderDevice } from '../../../hooks/useProviderDevice';
import { logger } from '../../../logger';

const FILTERS = {
  NONE: 'NONE',
  EXCHANGE_STOCK: 'EXCHANGE_STOCK',
  LOANER_WITH_CLINIC: 'LOANER_WITH_CLINIC',
  LOANER_IN_USE: 'LOANER_IN_USE',
  LOANER_IN_USE_WITHOUT_SR: 'LOANER_IN_USE_WITHOUT_SR',
  LOANER_RETURN_RECOMMENDED: 'LOANER_RETURN_RECOMMENDED',
  LOANER_RETURN_CRITICAL: 'LOANER_RETURN_CRITICAL',
};

const NewClinicEquipment = ({ rendering, country, lang }) => {
  const history = useHistory();
  const location = useLocation();
  const log = logger();

  // --- Provider devices (React Query) ---
  const {
    data: queryData,
    renderState,
    loanerDataRef,
    updateDeviceInRef,
    isSuccess,
    syncVersion,
    resetAndRefetch,
    isBackgroundFetching,
  } = useProviderDevices();

  const returnLoanerMutation = useReturnLoaner();

  // --- Notification ---
  const [notification, setNotification] = useState({
    message: '',
    variant: 'success',
  });

  const showNotification = useCallback(
    (message, variant = 'success') => {
      setNotification({ message, variant });
    },
    [],
  );

  // --- UI & table state ---
  const [loanerData, setLoanerData] = useState([]);
  const [tableKey, setTableKey] = useState(0); // forces DataTable remount on filter/reset

  // --- Modal state ---
  const [returnLoanerModalVisible, setReturnLoanerModalVisible] =
    useState(false);
  const [selectedDeviceForReturn, setSelectedDeviceForReturn] =
    useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // --- Filtering ---
  const activeFilterRef = useRef(FILTERS.NONE);

  const labels = useMemo(
    () => getLabelsForFields(rendering.fields),
    [rendering.fields],
  );

  const providerRegion = providerService.getCurrentProviderRegion();
  // After navigating back from assign-loaner: show success notification,
  // reset filter to ALL, and refetch with loading state.
  useEffect(() => {
    if (location.state?.assignLoanerSuccess) {
      activeFilterRef.current = FILTERS.NONE;
      setTableKey((k) => k + 1);
      resetAndRefetch(location.state?.assignedAssetId);
      history.replace({ ...location, state: undefined });

      showNotification(
        labels[
          'labels.clinicEquipment.notification.success.assignLoaner'
        ],
      );
    }
  }, [history, location, labels, showNotification, resetAndRefetch]);

  const applyFilter = useCallback((data, filter) => {
    switch (filter) {
      case FILTERS.EXCHANGE_STOCK:
        return data.filter(
          (d) => d.status === ASSET_STATUS.ExchangeStock,
        );
      case FILTERS.LOANER_WITH_CLINIC:
        return data.filter(
          (d) => d.loanerStatus === LOANER_STATUS.LoanerWithClinic,
        );
      case FILTERS.LOANER_IN_USE:
        return data.filter((d) =>
          [
            LOANER_STATUS.LoanerInUse,
            LOANER_STATUS.LoanerInUseWithoutSR,
          ].includes(d.loanerStatus),
        );
      case FILTERS.LOANER_IN_USE_WITHOUT_SR:
        return data.filter(
          (d) =>
            d.loanerStatus === LOANER_STATUS.LoanerInUseWithoutSR,
        );
      case FILTERS.LOANER_RETURN_RECOMMENDED:
        return data.filter(
          (d) =>
            d.loanerStatus === LOANER_STATUS.LoanerReturnRecommended,
        );
      case FILTERS.LOANER_RETURN_CRITICAL:
        return data.filter(
          (d) =>
            d.loanerStatus === LOANER_STATUS.LoanerReturnCritical,
        );
      default:
        return data;
    }
  }, []);

  // Sync hook data to local display state whenever it changes.
  useEffect(() => {
    if (isSuccess && loanerDataRef.current.length > 0) {
      setLoanerData(
        applyFilter(loanerDataRef.current, activeFilterRef.current),
      );
    }
  }, [isSuccess, queryData, applyFilter, loanerDataRef]);

  // --- Expanded row data ---
  const { getExpandedData } = useProviderDevice({
    labels,
    loanerDataRef,
    updateDeviceInRef,
  });

  const fetchExpandedData = useCallback(
    async (rData) => {
      const assetId = rData.id;
      const expanded = await getExpandedData(assetId);

      if (expanded && typeof expanded === 'object') {
        /* eslint-disable no-param-reassign */
        Object.keys(expanded).forEach((k) => {
          try {
            rData[k] = expanded[k];
          } catch (e) {
            log.warn('Failed to assign expanded field', {
              key: k,
              error: e,
            });
          }
        });
        /* eslint-enable no-param-reassign */

        setLoanerData((prev) =>
          prev.map((item) =>
            item.id === assetId ? { ...item, ...expanded } : item,
          ),
        );
      }

      return expanded;
    },
    [getExpandedData, log],
  );

  const applyFilterAndRefresh = useCallback(
    (filter) => {
      resetPreviousExpandedSections();
      activeFilterRef.current = filter;
      const filtered = applyFilter(loanerDataRef.current, filter);
      setLoanerData(filtered);
      setTableKey((k) => k + 1);
    },
    [applyFilter],
  );

  const filterLoaners = useCallback(
    (filterType) => {
      switch (filterType) {
        case 'exchangeStock':
          applyFilterAndRefresh(FILTERS.EXCHANGE_STOCK);
          break;
        case 'loanerWithClinic':
          applyFilterAndRefresh(FILTERS.LOANER_WITH_CLINIC);
          break;
        case 'loanerInUse':
          applyFilterAndRefresh(FILTERS.LOANER_IN_USE);
          break;
        case 'loanerInUseWithoutSR':
          applyFilterAndRefresh(FILTERS.LOANER_IN_USE_WITHOUT_SR);
          break;
        case 'loanerReturnRecommended':
          applyFilterAndRefresh(FILTERS.LOANER_RETURN_RECOMMENDED);
          break;
        case 'loanerReturnCritical':
          applyFilterAndRefresh(FILTERS.LOANER_RETURN_CRITICAL);
          break;
        default:
          applyFilterAndRefresh(FILTERS.NONE);
          break;
      }
    },
    [applyFilterAndRefresh],
  );

  const resetFilters = useCallback(() => {
    resetPreviousExpandedSections();
    activeFilterRef.current = FILTERS.NONE;
    setLoanerData(loanerDataRef.current);
    setTableKey((k) => k + 1);
  }, []);

  const handleReturnLoaner = useCallback((device) => {
    setSelectedDeviceForReturn(device);
    setNotification({ message: '', variant: 'success' });
    setReturnLoanerModalVisible(true);
  }, []);

  const handleReturnLoanerClose = useCallback(() => {
    setReturnLoanerModalVisible(false);
    setSelectedDeviceForReturn(null);
  }, []);

  const navigateWithDevice = useCallback(
    (route, device) => {
      history.push({
        pathname: `/${country}/${lang}/${route}`,
        state: { selectedLoaner: device },
      });
    },
    [country, lang, history],
  );

  const handleAssignLoaner = useCallback(
    (device) => navigateWithDevice(routes.dpx.assignLoaner, device),
    [navigateWithDevice],
  );

  const handleReportIssue = useCallback(
    (device) =>
      navigateWithDevice(routes.dpx.reportLoanerIssue, device),
    [navigateWithDevice],
  );

  const handleViewServiceRequest = useCallback(
    (device) => {
      const srId = device?.serviceRequest?.id;
      const srNumber = device?.serviceRequest?.number;
      const patientCochlearId = device?.owner?.cochlearId;
      history.push({
        pathname: `/${country}/${lang}/${routes.dpx.viewServiceRequest}`,
        state: {
          patientId: patientCochlearId,
          serviceRequestId: srId,
          serviceRequestNumber: srNumber,
          returnPath: `${location.pathname}`,
          source: 'clinic-equipment',
        },
      });
    },
    [country, lang, history, location],
  );

  const handleReturnLoanerSubmit = useCallback(async () => {
    if (!selectedDeviceForReturn) return;

    setIsSubmitting(true);

    try {
      const response = await returnLoanerMutation.mutateAsync({
        assetId: selectedDeviceForReturn.id,
        patientId: selectedDeviceForReturn.owner?.cochlearId,
      });

      if (!response?.success) {
        showNotification(
          labels[
            'labels.clinicEquipment.notification.error.returnLoaner'
          ],
          'error',
        );
        handleReturnLoanerClose();
        return;
      }

      const assetId = selectedDeviceForReturn.id;
      handleReturnLoanerClose();
      activeFilterRef.current = FILTERS.NONE;
      setTableKey((k) => k + 1);
      resetAndRefetch(assetId);
      showNotification(
        labels[
          'labels.clinicEquipment.notification.success.returnLoaner'
        ],
      );
    } catch (error) {
      log.error('Failed to return loaner device', error);
      showNotification(
        labels[
          'labels.clinicEquipment.notification.error.returnLoaner'
        ],
        'error',
      );
      handleReturnLoanerClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedDeviceForReturn,
    handleReturnLoanerClose,
    showNotification,
    returnLoanerMutation,
    resetAndRefetch,
    labels,
    log,
  ]);

  // syncVersion is used as a dependency trigger because loanerDataRef
  // is a ref and its mutations are not observable by React.
  const filterCounts = useMemo(() => {
    const data = loanerDataRef.current;
    return {
      exchangeStock: applyFilter(data, FILTERS.EXCHANGE_STOCK).length,
      loanerWithClinic: applyFilter(data, FILTERS.LOANER_WITH_CLINIC)
        .length,
      loanerInUse: applyFilter(data, FILTERS.LOANER_IN_USE).length,
      loanerInUseWithoutSR: applyFilter(
        data,
        FILTERS.LOANER_IN_USE_WITHOUT_SR,
      ).length,
      loanerReturnRecommended: applyFilter(
        data,
        FILTERS.LOANER_RETURN_RECOMMENDED,
      ).length,
      loanerReturnCritical: applyFilter(
        data,
        FILTERS.LOANER_RETURN_CRITICAL,
      ).length,
    };
  }, [loanerDataRef.current.length, syncVersion, applyFilter]);

  const toolBarConfig = useMemo(
    () =>
      clinicEquipmentConfig.getToolbarConfig({
        labels,
        filterLoaners,
        resetFilters,
        providerRegion,
        filterCounts,
        isBackgroundFetching,
      }),
    [
      labels,
      filterLoaners,
      resetFilters,
      providerRegion,
      filterCounts,
      isBackgroundFetching,
    ],
  );

  const formattedLoanerData = useMemo(
    () =>
      loanerData.map((loaner) => ({
        ...loaner,
        productType: loaner.product?.type || '',
        productDescription: loaner.product?.description || '',
        expandedDataL: loaner.expandedDataL || [],
        expandedDataR: loaner.expandedDataR || [],
        formattedSerialNumber: loaner.product?.serialNumber
          ? `${labels['labels.clinicEquipment.dataTable.columns.serialNumber']} ${loaner.product?.serialNumber}`
          : '',
        formattedStatus:
          labels[
            `labels.clinicEquipment.assetStatus.${loaner.status}`
          ] || loaner.status,
      })),
    [loanerData, labels],
  );

  const devicesTableConfig = useMemo(
    () =>
      clinicEquipmentConfig.getDevicesTableConfig({
        data: loanerDataRef.current,
        fetchExpandedData,
        labels,
        handleReportIssue,
        handleViewServiceRequest,
        handleAssignLoaner,
        handleReturnLoaner,
        providerRegion,
      }),
    [
      loanerData,
      fetchExpandedData,
      labels,
      handleReportIssue,
      handleViewServiceRequest,
      handleAssignLoaner,
      handleReturnLoaner,
      providerRegion,
    ],
  );

  return (
    <>
      <div className="ccl__container">
        <div className="ccl__main-clinic-equipment ccl__main-clinic-equipment--new">
          <PageHeader
            className="ccl__main-clinic-equipment__title"
            title={{
              content: labels['labels.clinicEquipment.header.title'],
            }}
          />
          {notification.message && (
            <Row>
              <Col xs={12}>
                <Alert
                  defaultShow
                  variant={notification.variant}
                  dismissible
                  heading=""
                  actions={[]}
                >
                  {notification.message}
                </Alert>
              </Col>
            </Row>
          )}
          <Row>
            {renderState !== 'loading' && (
              <CustomToolBar config={toolBarConfig} />
            )}
            <div className="ccl__data-table--dpx ccl__data-table--dpx--clinic-equipment">
              <DataTable
                key={tableKey}
                config={devicesTableConfig}
                data={formattedLoanerData}
                labels={labels}
                stateData={{
                  parameters: { searchTerm: '' },
                  renderState,
                }}
              />
            </div>
          </Row>
        </div>
      </div>
      <ReturnLoanerModal
        labels={labels}
        onClose={handleReturnLoanerClose}
        onSubmit={handleReturnLoanerSubmit}
        visible={returnLoanerModalVisible}
        isSubmitting={isSubmitting}
      />

    </>
  );
};

export default NewClinicEquipment;
